import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, KeyRound, Copy, Users } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const EMPTY = { name: "", email: "", phone: "", codice_fiscale: "", note: "" };

export default function AdminClients() {
  const [clients, setClients] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [tempPassword, setTempPassword] = useState(null);

  const load = () => api.get("/clients").then((r) => setClients(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, email: c.email, phone: c.phone || "", codice_fiscale: c.codice_fiscale, note: c.note || "" }); setDialogOpen(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/clients/${editing.id}`, form);
        toast.success("Cliente aggiornato");
      } else {
        const { data } = await api.post("/clients", form);
        setTempPassword({ client: data.name, email: data.email, password: data.temp_password });
        toast.success("Cliente creato");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/clients/${deleting.id}`);
      toast.success("Cliente eliminato");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleResetPassword = async (c) => {
    try {
      const { data } = await api.post(`/clients/${c.id}/reset-password`);
      setTempPassword({ client: c.name, email: c.email, password: data.temp_password });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(tempPassword.password);
    toast.success("Password copiata");
  };

  return (
    <AdminLayout title="Clienti" subtitle="Anagrafiche e credenziali di accesso all'area riservata">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-muted-foreground">
          {clients ? `${clients.length} clienti registrati` : "Caricamento..."}
        </p>
        <Button onClick={openCreate} data-testid="create-client-button" className="bg-navy hover:bg-navy-light text-white">
          <Plus className="w-4 h-4 mr-2" />
          Nuovo Cliente
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm overflow-hidden">
        {clients === null ? (
          <p className="text-sm text-muted-foreground p-12 text-center">Caricamento clienti...</p>
        ) : clients.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Nessun cliente. Crea il primo per iniziare a caricare gli F24.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="clients-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-slate-100 bg-slate-50/60">
                  <th className="px-6 py-3 font-semibold">Cliente</th>
                  <th className="px-6 py-3 font-semibold">Codice Fiscale / P.IVA</th>
                  <th className="px-6 py-3 font-semibold">Email</th>
                  <th className="px-6 py-3 font-semibold">Telefono</th>
                  <th className="px-6 py-3 font-semibold text-right">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {clients.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50/60 transition-colors duration-150" data-testid={`client-row-${c.codice_fiscale}`}>
                    <td className="px-6 py-4 font-medium text-navy">{c.name}</td>
                    <td className="px-6 py-4 font-mono text-xs">{c.codice_fiscale}</td>
                    <td className="px-6 py-4 text-muted-foreground">{c.email}</td>
                    <td className="px-6 py-4 text-muted-foreground">{c.phone || "—"}</td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleResetPassword(c)} title="Reimposta password" data-testid={`client-reset-password-${c.codice_fiscale}`}>
                          <KeyRound className="w-4 h-4 text-gold" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(c)} title="Modifica" data-testid={`client-edit-${c.codice_fiscale}`}>
                          <Pencil className="w-4 h-4 text-navy" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleting(c)} title="Elimina" data-testid={`client-delete-${c.codice_fiscale}`}>
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="client-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">{editing ? "Modifica Cliente" : "Nuovo Cliente"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="c-name">Nome / Ragione Sociale</Label>
              <Input id="c-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="es. Rossi Mario o Alfa S.r.l." data-testid="client-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="c-cf">Codice Fiscale / P.IVA</Label>
              <Input id="c-cf" required value={form.codice_fiscale} onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value.toUpperCase() })} placeholder="RSSMRA80A01F205X" className="font-mono uppercase" data-testid="client-cf-input" />
              <p className="text-xs text-muted-foreground">Deve corrispondere al codice nel nome dei file F24 (es. RSSMRA80A01F205X_ROSSI_2026-06_F24.pdf)</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="c-email">Email</Label>
                <Input id="c-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="client-email-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="c-phone">Telefono (per SMS/WhatsApp)</Label>
                <Input id="c-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+39 333 1234567" data-testid="client-phone-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="c-note">Note</Label>
              <Input id="c-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="client-note-input" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} data-testid="client-save-button" className="bg-navy hover:bg-navy-light text-white">
                {saving ? "Salvataggio..." : editing ? "Salva modifiche" : "Crea cliente e account"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={() => setDeleting(null)}>
        <AlertDialogContent data-testid="client-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare {deleting?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Verrà eliminato anche l'account di accesso e gli F24 associati non saranno più visibili.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="client-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="client-delete-confirm" className="bg-red-600 hover:bg-red-700">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!tempPassword} onOpenChange={() => setTempPassword(null)}>
        <DialogContent data-testid="temp-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">Credenziali di accesso</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Comunica queste credenziali a <strong>{tempPassword?.client}</strong>. La password temporanea non sarà più visibile dopo la chiusura.
          </p>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-2 font-mono text-sm">
            <p><span className="text-muted-foreground">Email:</span> {tempPassword?.email}</p>
            <div className="flex items-center justify-between gap-2">
              <p className="truncate"><span className="text-muted-foreground font-sans">Password:</span> <strong data-testid="temp-password-value">{tempPassword?.password}</strong></p>
              <Button variant="outline" size="sm" onClick={copyPassword} data-testid="copy-password-button">
                <Copy className="w-3.5 h-3.5 mr-1.5" />
                Copia
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setTempPassword(null)} data-testid="temp-password-close" className="bg-navy hover:bg-navy-light text-white">Ho annotato le credenziali</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
